import { useEffect, useState } from 'react';
import { Button, Input, Select, Space, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { Permission } from '@aid/shared';
import type { SolicitudResponse } from '../../api/types';
import { AdminListToolbar } from '../../components/AdminListToolbar';
import { AdminBreadcrumbs } from '../../components/AdminBreadcrumbs';
import { DataTable } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { useHasPermission } from '../../auth/useHasPermission';
import { useT } from '../../locales';
import { paginationProps } from '../../lib/paginacion';
import { serverSortProps, useServerPaginacion } from '../../lib/server-sort';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { ApproveSolicitudModal } from './components/ApproveSolicitudModal';
import { RejectSolicitudModal } from './components/RejectSolicitudModal';
import { useSolicitudesList } from './api/useSolicitudes';
import { StatusTag, type StatusTagTone } from '../../components/StatusTag';

const statusTones: Record<string, StatusTagTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  cancelled: 'neutral',
};

const statusLabelKeys: Record<string, string> = {
  pending: 'pages.solicitudes.pending',
  approved: 'pages.solicitudes.approved',
  rejected: 'pages.solicitudes.rejected',
  cancelled: 'pages.solicitudes.cancelled',
};

export function SolicitudesListPage() {
  const t = useT();
  const [status, setStatus] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [approveId, setApproveId] = useState<number | null>(null);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const debouncedSearch = useDebouncedValue(search.trim());
  const { page, pageSize, sort, handleChange, setPage } =
    useServerPaginacion<SolicitudResponse>(20);
  const canApprove = useHasPermission(Permission.SOLICITUDES_APPROVE);
  const canReject = useHasPermission(Permission.SOLICITUDES_REJECT);

  const { data, isLoading, isFetching, error, refetch } = useSolicitudesList({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    status,
    search: debouncedSearch || undefined,
    ...sort,
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, setPage]);

  const clearFilters = () => {
    setSearch('');
    setStatus(undefined);
    setPage(1);
  };

  const hasFilters = Boolean(debouncedSearch || status);
  const emptyState = (
    <EmptyState
      title={t(
        hasFilters ? 'pages.solicitudes.emptyFilteredTitle' : 'pages.solicitudes.emptyTitle',
      )}
      description={t(
        hasFilters
          ? 'pages.solicitudes.emptyFilteredDescription'
          : 'pages.solicitudes.emptyDescription',
      )}
      action={hasFilters ? <Button onClick={clearFilters}>{t('common.clear')}</Button> : undefined}
    />
  );

  const columns = [
    {
      title: t('common.id'),
      dataIndex: 'id' as keyof SolicitudResponse,
      key: 'id',
      ...serverSortProps('id', sort),
      width: 80,
    },
    {
      title: t('common.email'),
      dataIndex: 'email' as keyof SolicitudResponse,
      key: 'email',
      ...serverSortProps('email', sort),
    },
    {
      title: t('pages.solicitudes.keycloakId'),
      dataIndex: 'keycloakId' as keyof SolicitudResponse,
      key: 'keycloakId',
      ellipsis: true,
    },
    {
      title: t('common.status'),
      dataIndex: 'estado' as keyof SolicitudResponse,
      key: 'estado',
      ...serverSortProps('estado', sort),
      render: (estado: string) => {
        const labelKey = statusLabelKeys[estado];
        return (
          <StatusTag tone={statusTones[estado] ?? 'neutral'}>
            {labelKey ? t(labelKey) : estado}
          </StatusTag>
        );
      },
    },
    {
      title: t('pages.solicitudes.message'),
      dataIndex: 'mensaje' as keyof SolicitudResponse,
      key: 'mensaje',
      render: (value: string | null) => value ?? '-',
      ellipsis: true,
    },
    {
      title: t('pages.solicitudes.requestedAt'),
      dataIndex: 'solicitadoEn' as keyof SolicitudResponse,
      key: 'solicitadoEn',
      ...serverSortProps('solicitadoEn', sort),
      render: (value: Date) => new Date(value).toLocaleDateString(),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_value: unknown, record: SolicitudResponse) => {
        if (record.estado !== 'pending') {
          return null;
        }

        if (!canApprove && !canReject) {
          return (
            <Typography.Text type="secondary">{t('common.actionsNotAvailable')}</Typography.Text>
          );
        }

        return (
          <Space wrap size={[8, 8]}>
            {canApprove && (
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => setApproveId(record.id)}
              >
                {t('pages.solicitudes.approve')}
              </Button>
            )}
            {canReject && (
              <Button
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => setRejectId(record.id)}
              >
                {t('pages.solicitudes.reject')}
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        breadcrumb={<AdminBreadcrumbs current={t('pages.solicitudes.title')} />}
        title={t('pages.solicitudes.title')}
        subtitle={t('pages.solicitudes.subtitle')}
      />
      <AdminListToolbar
        filters={
          <>
            <Input
              aria-label={t('pages.solicitudes.searchPlaceholder')}
              placeholder={t('pages.solicitudes.searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              allowClear
              style={{ width: 300, maxWidth: '100%' }}
            />
            <Select
              aria-label={t('pages.solicitudes.statusFilter')}
              placeholder={t('pages.solicitudes.statusFilter')}
              allowClear
              value={status}
              onChange={(value: string | undefined) => {
                setStatus(value);
                setPage(1);
              }}
              options={Object.entries(statusLabelKeys).map(([value, labelKey]) => ({
                value,
                label: t(labelKey),
              }))}
              style={{ width: 190, maxWidth: '100%' }}
            />
          </>
        }
      />
      {error ? (
        <EmptyState
          title={t('pages.solicitudes.errorLoad')}
          description={t('common.error')}
          action={<Button onClick={() => void refetch()}>{t('common.retry')}</Button>}
        />
      ) : (
        <DataTable<SolicitudResponse>
          columns={columns}
          dataSource={data?.data ?? []}
          rowKey="id"
          loading={isLoading || isFetching}
          pagination={paginationProps(t, { page, pageSize, total: data?.total ?? 0 })}
          onChange={handleChange}
          locale={{ emptyText: emptyState }}
        />
      )}
      {approveId !== null && (
        <ApproveSolicitudModal solicitudId={approveId} open onClose={() => setApproveId(null)} />
      )}
      {rejectId !== null && (
        <RejectSolicitudModal solicitudId={rejectId} open onClose={() => setRejectId(null)} />
      )}
    </div>
  );
}
